import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, ShieldCheck } from 'lucide-react';
import { etsyAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { TennisPriceInputs } from './TennisPriceInputs';
import {
  IGI_UPCHARGE, TENNIS_CONFIG, tennisCarats, tennisPricesComplete, tennisPricesPayload,
} from '../../lib/tennis';

const fmt = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const toDraft = (p, pt) => Object.fromEntries(tennisCarats(pt).map((c) => [c, p?.[c] ?? p?.[String(c)] ?? '']));

const TennisPriceEditor = ({ product, onUpdate }) => {
  const pt = product.product_type;
  const [draft, setDraft] = useState(toDraft(product?.tennis_prices, pt));
  const [saving, setSaving] = useState(false);
  const savedKey = JSON.stringify(product?.tennis_prices || {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft(toDraft(product?.tennis_prices, pt)); }, [savedKey, pt]);

  const save = async () => {
    if (!tennisPricesComplete(draft, pt)) { toast.error('Enter a price for every carat'); return; }
    try {
      setSaving(true);
      const res = await etsyAPI.editPricing(product.id, { tennis_prices: tennisPricesPayload(draft, pt) });
      onUpdate?.({ ...product, tennis_prices: res.data.tennis_prices });
      toast.success(`${TENNIS_CONFIG[pt].label} prices saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-emerald-50/40 border border-emerald-100 rounded-lg p-3 space-y-3" data-testid="tennis-price-editor">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800">
        Final Etsy price · non-certified · no sale markup
      </div>
      <TennisPriceInputs productType={pt} value={draft} onChange={setDraft} testIdPrefix="tennis-edit-price" inputClassName="bg-white" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] text-zinc-500">
          IGI certified adds +{fmt(IGI_UPCHARGE)} · same price for 14K White &amp; Yellow
          {TENNIS_CONFIG[pt].priceNote && <> · {TENNIS_CONFIG[pt].priceNote}</>}
        </span>
        <Button size="sm" onClick={save} disabled={saving} className="h-8 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="tennis-save-prices-btn">
          {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />} Save prices
        </Button>
      </div>
    </div>
  );
};

const TennisVariationTable = ({ productType, prices }) => {
  const carats = tennisCarats(productType);
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200">
      <table className="w-full text-sm" data-testid="tennis-variation-table">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
            <th className="text-left px-3 py-2.5">Total carat weight</th>
            <th className="text-right px-3 py-2.5">No Cert</th>
            <th className="text-right px-3 py-2.5"><span className="inline-flex items-center gap-1"><ShieldCheck size={11} /> IGI Cert</span></th>
          </tr>
        </thead>
        <tbody>
          {carats.map((c) => {
            const p = Number(prices?.[String(c)]);
            return (
              <tr key={c} className="border-b border-zinc-100 last:border-b-0">
                <td className="px-3 py-2 font-medium text-zinc-900">{c} CT</td>
                <td className="px-3 py-2 text-right font-mono font-semibold" data-testid={`tennis-cell-${c}-nocert`}>{p > 0 ? fmt(p) : '—'}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold" data-testid={`tennis-cell-${c}-igi`}>{p > 0 ? fmt(p + IGI_UPCHARGE) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 bg-zinc-50/60 border-t border-zinc-100 text-[11px] text-zinc-500">
        Etsy dropdowns: <strong>Total Carat Weight</strong> × <strong>Metal &amp; Certificate</strong> (14K White / Yellow · No Cert / IGI Cert) = {carats.length * 4} variations
      </div>
    </div>
  );
};

const TennisSpecsEditor = ({ product, onUpdate }) => {
  const fields = TENNIS_CONFIG[product.product_type].specFields;
  const saved = product?.tennis_specs || {};
  const [specs, setSpecs] = useState(() => Object.fromEntries(fields.map((f) => [f.key, saved[f.key] || f.def || ''])));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      const res = await etsyAPI.editPricing(product.id, { tennis_specs: specs });
      onUpdate?.({ ...product, tennis_specs: res.data.tennis_specs });
      toast.success('Specs saved — regenerate the description to apply them');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save specs');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-200 rounded-lg p-3 space-y-3" data-testid="tennis-specs-editor">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{TENNIS_CONFIG[product.product_type].label} specs · used in the Etsy description</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {fields.map((f) => (
          <label key={f.key} className="block">
            <span className="block text-[11px] font-semibold text-zinc-600 mb-1">{f.label}</span>
            <Input
              value={specs[f.key]}
              onChange={(e) => setSpecs((s) => ({ ...s, [f.key]: e.target.value }))}
              placeholder={f.placeholder || f.def}
              className="h-8 text-xs"
              data-testid={`tennis-spec-${f.key}`}
            />
          </label>
        ))}
      </div>
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={save} disabled={saving} className="h-8" data-testid="tennis-save-specs-btn">
          {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />} Save specs
        </Button>
      </div>
    </div>
  );
};

export const TennisPricingPanel = ({ product, onUpdate }) => (
  <div className="space-y-4" data-testid="tennis-pricing-panel">
    <TennisPriceEditor product={product} onUpdate={onUpdate} />
    {!tennisPricesComplete(toDraft(product?.tennis_prices, product.product_type), product.product_type) && (
      <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="tennis-prices-missing">
        Enter all {tennisCarats(product.product_type).length} prices and save before pushing to Etsy.
      </div>
    )}
    <TennisVariationTable productType={product.product_type} prices={product?.tennis_prices} />
    <TennisSpecsEditor key={product.product_type} product={product} onUpdate={onUpdate} />
  </div>
);
